import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { LogIn, LogOut, Clock, QrCode, Receipt, Plus, Wallet, Coffee, Repeat, CheckSquare, Square, AlertTriangle, Gift } from 'lucide-react';
import axios from 'axios';
import QrScannerModal from './QrScannerModal';
import { TaskAttachments } from './TaskAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }); } catch { return '-'; }
};
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR'); } catch { return d; }
};
const formatDuration = (mins) => {
  if (mins == null) return '-';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h} sa ${m} dk`;
};

const PersonnelMesaiBanner = () => {
  const [status, setStatus] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const [scanMode, setScanMode] = useState('work'); // 'work' | 'break'
  const [reasonModal, setReasonModal] = useState(null); // {code, scheduled, qrToken}
  const [reasonText, setReasonText] = useState('');
  const [reasonSaving, setReasonSaving] = useState(false);

  const token = useMemo(() => localStorage.getItem('token'), []);
  const authConfig = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const personnelId = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.personnel_id || null; } catch { return null; }
  }, []);

  // Harcama (cep harcaması) state
  const todayStr = new Date().toISOString().slice(0, 10);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [expForm, setExpForm] = useState({ amount: '', description: '', date: todayStr });
  const [mySummary, setMySummary] = useState(null);
  const [savingExp, setSavingExp] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [taskView, setTaskView] = useState('active'); // 'active' | 'done'
  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/personnel/me/tasks`, authConfig);
      setTasks(res.data?.items || []);
    } catch (e) { /* sessiz */ }
  };
  const completeTask = async (id) => {
    try {
      await axios.put(`${API}/personnel-tasks/${id}/status`, { status: 'done' }, authConfig);
      fetchTasks();
    } catch (e) { /* sessiz */ }
  };
  const activeTasks = useMemo(() => tasks.filter(t => t.status !== 'done'), [tasks]);
  const doneTasks = useMemo(() => tasks.filter(t => t.status === 'done'), [tasks]);
  const visibleTasks = taskView === 'done' ? doneTasks : activeTasks;
  // Günlük rutinler
  const [routines, setRoutines] = useState([]);
  const [routinesOnLeave, setRoutinesOnLeave] = useState(false);
  const fetchRoutines = async () => {
    try {
      const res = await axios.get(`${API}/personnel/me/routines`, authConfig);
      setRoutines(res.data?.items || []);
      setRoutinesOnLeave(!!res.data?.on_leave);
    } catch (e) { /* sessiz */ }
  };
  const toggleRoutine = async (r) => {
    try {
      if (r.completed) {
        await axios.delete(`${API}/personnel/me/routines/${r.routine_id}/complete`, authConfig);
      } else {
        await axios.post(`${API}/personnel/me/routines/${r.routine_id}/complete`, {}, authConfig);
      }
      fetchRoutines();
    } catch (e) { toast.error('İşlem başarısız'); }
  };
  useEffect(() => { fetchTasks(); fetchRoutines(); /* eslint-disable-next-line */ }, []);


  const fetchExpenses = async () => {
    if (!personnelId) return;
    try {
      const res = await axios.get(`${API}/personnel-expenses/${personnelId}`, authConfig);
      setExpenses(res.data || []);
    } catch (e) { /* sessiz */ }
  };

  const addExpense = async () => {
    const amt = parseFloat(expForm.amount);
    if (!expForm.description.trim() || isNaN(amt) || amt <= 0) {
      toast.error('Lütfen geçerli bir tutar ve harcama nedeni girin');
      return;
    }
    setSavingExp(true);
    try {
      await axios.post(`${API}/personnel-expenses`, {
        personnel_id: personnelId,
        amount: amt,
        description: expForm.description.trim(),
        date: expForm.date || todayStr,
      }, authConfig);
      toast.success('Harcama kaydedildi (yönetici onayı bekliyor)');
      setExpForm({ amount: '', description: '', date: todayStr });
      fetchExpenses();
      fetchMySummary();
    } catch (e) {
      toast.error('Harcama kaydedilemedi');
    } finally {
      setSavingExp(false);
    }
  };

  // Canlı geçen süre (status null olabilir, bu yüzden hooks'u erken çağrı dışında tutuyoruz)
  const elapsed = useMemo(() => {
    const checkIn = status?.open_record?.check_in_time;
    if (!checkIn) return 0;
    try {
      const start = new Date(checkIn);
      return Math.floor((now - start) / 60000);
    } catch { return 0; }
  }, [status, now]);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/attendance/self/status`, authConfig);
      setStatus(res.data);
    } catch (e) { /* sessizce yoksay (admin için 403) */ }
  };

  const fetchMySummary = async () => {
    try {
      const res = await axios.get(`${API}/bonuses/my-summary`, authConfig);
      setMySummary(res.data);
    } catch (e) { /* sessiz (admin için 403) */ }
  };

  useEffect(() => {
    fetchStatus();
    fetchExpenses();
    fetchMySummary();
    const tickStatus = setInterval(fetchStatus, 30000);
    const tickClock = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(tickStatus); clearInterval(tickClock); };
  }, []);

  const openWorkScan = () => { setScanMode('work'); setShowScanner(true); };
  const openBreakScan = () => { setScanMode('break'); setShowScanner(true); };

  const handleScanResult = async (decodedText) => {
    setShowScanner(false);
    setLoading(true);
    try {
      if (scanMode === 'break') {
        const res = await axios.post(`${API}/attendance/break`, { qr_token: decodedText }, authConfig);
        if (res.data?.action === 'break_start') {
          toast.success('Molaya çıkış kaydedildi ☕');
        } else {
          toast.success(`Moladan dönüş kaydedildi ✓ (${formatDuration(res.data.break_minutes)})`);
        }
      } else {
        const res = await axios.post(`${API}/attendance/check`, { qr_token: decodedText }, authConfig);
        if (res.data?.action === 'check_in') {
          toast.success(`Giriş kaydedildi ✓ (${formatTime(res.data.check_in_time)})`);
        } else {
          toast.success(`Çıkış kaydedildi (${res.data.duration_text})`);
        }
      }
      await fetchStatus();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && (detail.code === 'LATE_REASON_REQUIRED' || detail.code === 'EARLY_LEAVE_REASON_REQUIRED')) {
        setReasonText('');
        setReasonModal({ code: detail.code, scheduled: detail.scheduled_start || detail.scheduled_end, qrToken: decodedText });
      } else {
        const msg = typeof detail === 'string' ? detail : 'İşlem başarısız';
        if (msg.includes('Geçersiz QR')) {
          toast.error('Geçersiz QR kod — ofis girişindeki resmi QR kodu okuttuğunuzdan emin olun');
        } else {
          toast.error(msg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const submitReason = async () => {
    if (!reasonText.trim()) { toast.error('Lütfen bir açıklama yazın'); return; }
    setReasonSaving(true);
    try {
      const res = await axios.post(`${API}/attendance/check`, { qr_token: reasonModal.qrToken, reason: reasonText.trim() }, authConfig);
      if (res.data?.action === 'check_in') {
        toast.success('Giriş kaydedildi ✓ (geç giriş açıklamanız iletildi)');
      } else {
        toast.success('Çıkış kaydedildi ✓ (erken çıkış açıklamanız iletildi)');
      }
      setReasonModal(null);
      setReasonText('');
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setReasonSaving(false);
    }
  };

  if (!status && !personnelId) return null;

  const isCheckedIn = status?.is_checked_in;
  const openRecord = status?.open_record;
  const todayMinutes = status?.today_total_minutes || 0;
  const onBreak = status?.on_break;
  const todayBreak = status?.today_break_minutes || 0;

  return (
    <div
      className={`mb-4 rounded-xl shadow-lg border-2 overflow-hidden ${
        isCheckedIn
          ? 'border-emerald-400 bg-gradient-to-r from-emerald-50 via-emerald-100 to-teal-50'
          : 'border-cyan-300 bg-gradient-to-r from-cyan-50 via-blue-50 to-cyan-50'
      }`}
      data-testid="personnel-mesai-banner"
    >
      {/* ÜST ŞERİT — Bu ayın primi ve harcamaları */}
      {mySummary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2.5 bg-white/70 border-b border-slate-200/70" data-testid="personnel-summary-strip">
          <div className="rounded-lg bg-[#0B172A] text-white p-3 shadow-sm flex items-center gap-3 border border-[#0B172A]" data-testid="my-bonus-card">
            <div className="p-2 rounded-lg bg-white/10">
              <Gift className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-white/60">Bu Ay Kazandığım Prim</p>
              <p className="text-2xl font-bold leading-tight tabular-nums" data-testid="my-bonus-amount">
                ₺{(mySummary.bonus?.amount || 0).toLocaleString('tr-TR')}
              </p>
              <p className="text-[11px] text-white/80">
                {mySummary.bonus?.paid ? 'Ödendi ✓' : (mySummary.bonus?.amount > 0 ? 'Ödeme bekliyor' : 'Bu ay için prim oluşmadı')}
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-white text-slate-800 p-3 shadow-sm flex items-center gap-3 border border-[#E2E8F0]" data-testid="my-expenses-card">
            <div className="p-2 rounded-lg bg-sky-50">
              <Wallet className="w-5 h-5 text-[#176B91]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wider text-slate-500">Bu Ay Yaptığım Harcamalar</p>
              <p className="text-2xl font-bold leading-tight tabular-nums" data-testid="my-expenses-total">
                ₺{(mySummary.expenses?.total || 0).toLocaleString('tr-TR')}
              </p>
              <p className="text-[11px] text-slate-500">
                {mySummary.expenses?.count || 0} kayıt • iade edilen ₺{(mySummary.expenses?.reimbursed || 0).toLocaleString('tr-TR')}
                {(mySummary.expenses?.pending || 0) > 0 && <span className="text-amber-700"> • bekleyen ₺{mySummary.expenses.pending.toLocaleString('tr-TR')}</span>}
              </p>
            </div>
          </div>
        </div>
      )}


      <div className="flex flex-col md:flex-row items-stretch">
        {/* SOL — Durum bilgisi */}
        <div className="flex-1 p-3 md:p-4 flex items-center gap-3">
          <div className={`p-2.5 rounded-lg ${isCheckedIn ? 'bg-emerald-500' : 'bg-cyan-500'} text-white shadow`}>
            <Clock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Mesai Durumu</span>
              {isCheckedIn ? (
                <>
                  <Badge className="bg-emerald-500 text-white border-0 text-[10px] gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                    ÇALIŞIYORSUNUZ
                  </Badge>
                  {onBreak && (
                    <Badge className="bg-amber-500 text-white border-0 text-[10px] gap-1" data-testid="banner-onbreak-badge">
                      <Coffee className="w-3 h-3" /> MOLADA
                    </Badge>
                  )}
                </>
              ) : (
                <Badge className="bg-slate-300 text-slate-700 border-0 text-[10px]">
                  {status?.shift_count > 0 ? 'Vardiya tamamlandı' : 'Henüz giriş yok'}
                </Badge>
              )}
            </div>
            {isCheckedIn ? (
              <div className="text-sm">
                <span className="text-slate-600">Giriş:</span>
                <span className="font-bold font-mono text-emerald-700 ml-1">{formatTime(openRecord.check_in_time)}</span>
                <span className="text-slate-400 mx-2">•</span>
                <span className="text-slate-600">Geçen süre:</span>
                <span className="font-bold text-blue-700 ml-1" data-testid="banner-elapsed">{formatDuration(elapsed)}</span>
                {todayBreak > 0 && (
                  <>
                    <span className="text-slate-400 mx-2">•</span>
                    <span className="text-slate-600">Mola:</span>
                    <span className="font-bold text-amber-700 ml-1" data-testid="banner-break">{formatDuration(todayBreak)}</span>
                  </>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-600">
                {status?.shift_count > 0 ? (
                  <>Bugün toplam <span className="font-bold text-blue-700">{formatDuration(todayMinutes)}</span> çalıştınız</>
                ) : (
                  <>Mesainize başlamak için QR kodu okutun</>
                )}
              </div>
            )}
          </div>
        </div>

        {/* SAĞ — Aksiyon butonları */}
        <div className="p-3 md:p-4 md:pl-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:border-l md:border-slate-200/50">
          <Button
            onClick={() => { fetchExpenses(); setShowExpenseModal(true); }}
            size="lg"
            variant="outline"
            className="h-12 text-sm font-semibold border-cyan-300 text-cyan-800 hover:bg-cyan-100 bg-white/60"
            data-testid="banner-expense-btn"
          >
            <Receipt className="w-4 h-4 mr-2" /> Harcama Ekle
          </Button>
          {isCheckedIn && (
            <Button
              onClick={openBreakScan}
              disabled={loading}
              size="lg"
              className={`h-12 text-sm font-bold shadow-md ${
                onBreak
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white'
                  : 'bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white'
              }`}
              data-testid="banner-break-btn"
            >
              <Coffee className="w-5 h-5 mr-2" /> {onBreak ? 'MOLADAN DÖN' : 'MOLAYA ÇIK'} <QrCode className="w-4 h-4 ml-2 opacity-70" />
            </Button>
          )}
          <Button
            onClick={openWorkScan}
            disabled={loading}
            size="lg"
            className={`w-full md:w-auto md:min-w-[180px] h-12 text-sm md:text-base font-bold shadow-md ${
              isCheckedIn
                ? 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white'
                : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white'
            }`}
            data-testid="banner-mesai-btn"
          >
            {loading ? (
              <span className="animate-pulse">İşleniyor...</span>
            ) : isCheckedIn ? (
              <><LogOut className="w-5 h-5 mr-2" /> ÇIKIŞ YAP <QrCode className="w-4 h-4 ml-2 opacity-70" /></>
            ) : (
              <><LogIn className="w-5 h-5 mr-2" /> GİRİŞ YAP <QrCode className="w-4 h-4 ml-2 opacity-70" /></>
            )}
          </Button>
        </div>
      </div>

      {/* Görevlerim */}
      {tasks.length > 0 && (
        <div className="mt-3 bg-white/70 rounded-xl border border-indigo-200 p-3" data-testid="personnel-tasks-card">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-sm font-bold text-indigo-800 flex items-center gap-1.5">
              <Receipt className="w-4 h-4" /> Görevlerim
            </p>
            <div className="flex rounded-lg overflow-hidden border border-indigo-200" data-testid="task-view-toggle">
              <button
                type="button"
                onClick={() => setTaskView('active')}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${taskView === 'active' ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-700 hover:bg-indigo-50'}`}
                data-testid="task-view-active"
              >
                Aktif Görevler ({activeTasks.length})
              </button>
              <button
                type="button"
                onClick={() => setTaskView('done')}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors border-l border-indigo-200 ${taskView === 'done' ? 'bg-emerald-600 text-white' : 'bg-white text-emerald-700 hover:bg-emerald-50'}`}
                data-testid="task-view-done"
              >
                Tamamlanan ({doneTasks.length})
              </button>
            </div>
          </div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {visibleTasks.length === 0 ? (
              <p className="text-xs text-slate-500 py-3 text-center" data-testid="task-list-empty">
                {taskView === 'active' ? 'Aktif göreviniz yok. Harika iş! 🎉' : 'Henüz tamamlanan göreviniz yok.'}
              </p>
            ) : visibleTasks.map((t) => (
              <div key={t.id} className={`rounded-lg border p-2.5 ${t.status === 'done' ? 'bg-slate-50 border-slate-200' : 'bg-white border-indigo-100'}`} data-testid={`task-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold ${t.status === 'done' ? 'line-through text-slate-400' : 'text-slate-800'}`}>{t.title}</p>
                    {t.description && <p className="text-xs text-slate-500 whitespace-pre-wrap">{t.description}</p>}
                    {t.due_date && <p className="text-[11px] text-rose-600 mt-0.5">Son Tarih: {t.due_date}</p>}
                    {t.status === 'done' && t.completed_at && (
                      <p className="text-[11px] text-emerald-600 mt-0.5">Tamamlandı: {String(t.completed_at).slice(0, 10)}</p>
                    )}
                  </div>
                  {t.status !== 'done' ? (
                    <Button size="sm" onClick={() => completeTask(t.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0" data-testid={`task-complete-${t.id}`}>Tamamla</Button>
                  ) : (
                    <span className="text-xs font-semibold text-emerald-600 shrink-0">✓ Tamamlandı</span>
                  )}
                </div>
                <TaskAttachments taskId={t.id} canManage />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Günlük Rutinlerim */}
      {(routines.length > 0 || routinesOnLeave) && (
        <div className="mt-3 bg-white/70 rounded-xl border border-emerald-200 p-3" data-testid="personnel-routines-card">
          <p className="text-sm font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
            <Repeat className="w-4 h-4" /> Günlük Rutinlerim {!routinesOnLeave && `(${routines.filter(r => !r.completed).length} bekliyor)`}
          </p>
          {routinesOnLeave ? (
            <p className="text-xs text-slate-500 py-2">Bugün izinlisiniz, rutin işleriniz kapalı. İyi dinlenmeler! 🌴</p>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {routines.map((r) => (
                <button
                  key={r.routine_id}
                  type="button"
                  onClick={() => toggleRoutine(r)}
                  className={`w-full flex items-center gap-2 rounded-lg border p-2.5 text-left transition-all ${r.completed ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200 hover:border-emerald-300'}`}
                  data-testid={`routine-check-${r.routine_id}`}
                >
                  {r.completed
                    ? <CheckSquare className="w-5 h-5 text-emerald-600 shrink-0" />
                    : <Square className="w-5 h-5 text-slate-300 shrink-0" />}
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm font-medium ${r.completed ? 'line-through text-slate-400' : 'text-slate-800'}`}>{r.title}</span>
                    {r.latest_time && (
                      <span className="text-[11px] text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> en geç {r.latest_time}
                      </span>
                    )}
                  </span>
                  {r.completed && r.status === 'gec' && (
                    <span className="text-[10px] font-semibold text-amber-600 flex items-center gap-0.5 shrink-0"><AlertTriangle className="w-3 h-3" /> Geç</span>
                  )}
                  {r.completed && (r.status === 'zamaninda' || r.status === 'yapildi') && (
                    <span className="text-[10px] font-semibold text-emerald-600 shrink-0">✓ Yapıldı</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Harcama Ekle Modalı */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="personnel-expense-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-cyan-600" /> Harcamalarım</DialogTitle>
            <DialogDescription>Yaptığınız harcamayı nedeni ve tarihiyle girin. Yönetici onayladığında size geri ödenir.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Harcama Nedeni</Label>
              <Input
                value={expForm.description}
                onChange={(e) => setExpForm({ ...expForm, description: e.target.value })}
                placeholder="Ör: Malzeme alımı, ulaşım, yemek..."
                data-testid="expense-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tutar (₺)</Label>
                <Input type="number" step="0.01" inputMode="decimal" value={expForm.amount}
                  onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })}
                  placeholder="0" data-testid="expense-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Tarih</Label>
                <Input type="date" value={expForm.date}
                  onChange={(e) => setExpForm({ ...expForm, date: e.target.value })}
                  data-testid="expense-date-input" />
              </div>
            </div>
            <Button onClick={addExpense} disabled={savingExp} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="expense-add-btn">
              <Plus className="w-4 h-4 mr-1" /> {savingExp ? 'Kaydediliyor...' : 'Harcama Ekle'}
            </Button>

            <div className="pt-2 border-t">
              <p className="text-xs font-semibold text-slate-500 mb-2">Geçmiş Harcamalarım</p>
              {expenses.length === 0 ? (
                <p className="text-slate-400 text-center py-3 text-sm">Henüz harcama eklemediniz</p>
              ) : (
                <div className="max-h-56 overflow-y-auto space-y-1.5">
                  {expenses.map((ex) => (
                    <div key={ex.id} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2 text-sm" data-testid={`expense-item-${ex.id}`}>
                      <div className="min-w-0">
                        <p className="text-slate-800 font-medium truncate">{ex.description}</p>
                        <p className="text-slate-400 text-xs">{formatDate(ex.date)}</p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-slate-800 font-bold">₺{(ex.amount || 0).toLocaleString('tr-TR')}</p>
                        {ex.reimbursed
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[9px] gap-0.5"><Wallet className="w-2.5 h-2.5" /> Ödendi</Badge>
                          : <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px]">Onay bekliyor</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <QrScannerModal
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleScanResult}
        title={scanMode === 'break'
          ? (onBreak ? 'Moladan Dönüş için QR Okutun' : 'Molaya Çıkış için QR Okutun')
          : (isCheckedIn ? 'Çıkış için QR Kodu Okutun' : 'Giriş için QR Kodu Okutun')}
        description="Ofis girişindeki QR kodunu çerçeve içine yerleştirin. Otomatik olarak okunacaktır."
      />

      {/* Geç giriş / erken çıkış açıklama modalı */}
      <Dialog open={!!reasonModal} onOpenChange={(v) => { if (!v) setReasonModal(null); }}>
        <DialogContent className="max-w-md" data-testid="attendance-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Clock className="w-5 h-5" /> {reasonModal?.code === 'EARLY_LEAVE_REASON_REQUIRED' ? 'Erken Çıkış Açıklaması' : 'Geç Giriş Açıklaması'}
            </DialogTitle>
            <DialogDescription>
              {reasonModal?.code === 'EARLY_LEAVE_REASON_REQUIRED'
                ? `Mesai bitiş saatiniz ${reasonModal?.scheduled || ''}. Erken çıkış yaptığınız için lütfen kısa bir açıklama yazınız.`
                : `Bugünkü mesai başlangıcınız ${reasonModal?.scheduled || ''}. Geç giriş yaptığınız için lütfen kısa bir açıklama yazınız.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 leading-relaxed">
              ⚠️ <strong>Değerli mesai arkadaşımız</strong>, çalışma saatlerine özen göstermeniz kurumsal disiplinimiz açısından önemlidir. Bu açıklama yöneticinize iletilecektir. Anlayışınız için teşekkür ederiz.
            </div>
            <div>
              <Label className="text-xs">Açıklama *</Label>
              <textarea
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="Ör: Trafik yoğunluğu, sağlık durumu, resmi işlem..."
                className="w-full min-h-[90px] px-3 py-2 border border-slate-200 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="attendance-reason-input"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setReasonModal(null)} className="flex-1" disabled={reasonSaving}>Vazgeç</Button>
              <Button onClick={submitReason} disabled={reasonSaving} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="attendance-reason-submit">
                {reasonSaving ? 'Gönderiliyor...' : 'Açıklamayı Gönder'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonnelMesaiBanner;
